/**
 * Bay Service
 * 
 * API service layer for bay-related operations.
 * 
 * **MIGRATION STATUS**:
 * - `getAvailableBays()` - ✅ Migrated to standard ApiResponse<T> format
 * - `getClubAvailability()` - ✅ Migrated to standard ApiResponse<T> format
 *
 * @see /apps/api/src/api/public/v1/controllers/bayController.ts
 */

import { apiClient } from '~/lib/apiClient/apiClient';
import type {
  ApiBayAvailabilityData,
  ApiAvailableBaysRequest,
  DurationBayAvailabilityPayload,
} from '@lgc/database/types';

/** The availability payload — the inner data of the (now enveloped) availability-with-states response. */
type ClubAvailabilityPayload = ApiBayAvailabilityData;

/**
 * Get available bays for a club with duration-based availability checking
 * 
 * Returns bay availability for an entire booking duration (not just a single point in time).
 * Includes detailed restriction information for unavailable bays.
 * 
 * **Duration-Based Checking:**
 * Validates that bays are available for the ENTIRE booking window by checking:
 * - Booking conflicts across the duration
 * - Club operating hours
 * - Membership-only hours
 * - Custom booking restrictions
 * - Prime time periods
 * 
 * **NEW FORMAT**: Uses standard ApiResponse<T> format with full type safety.
 * Returns ApiResult<DurationBayAvailabilityPayload> discriminated union.
 * 
 * @param clubId - Numeric club ID
 * @param params - Query parameters (startTime, endTime, userId, bayIds, etc.)
 * @returns ApiResult with DurationBayAvailabilityPayload on success
 * 
 * @example
 * ```typescript
 * // Duration-based check (2-hour booking)
 * // Client converts club local time to UTC using createClubDateTime().toISOString()
 * const result = await bayService.getAvailableBays(2, {
 *   startTime: '2024-03-15T20:00:00.000Z', // 2 PM MST in UTC
 *   endTime: '2024-03-15T22:00:00.000Z',   // 4 PM MST in UTC
 *   userId: 123,
 *   bayIds: [1, 3, 5], // Optional: filter specific bays
 * });
 * 
 * if (result.success) {
 *   const availableBays = result.payload.items.filter(b => b.isAvailable);
 *   console.log(`${availableBays.length} of ${result.payload.total} bays available`);
 * } else {
 *   console.error(result.message);
 * }
 * ```
 */
export async function getAvailableBays(
  clubId: number,
  params?: ApiAvailableBaysRequest
) {
  const queryParams = new URLSearchParams();
  
  // Duration-based parameters (required)
  if (params?.startTime) {
    queryParams.append('startTime', params.startTime);
  }
  
  if (params?.endTime) {
    queryParams.append('endTime', params.endTime);
  }
  
  // User-aware availability
  if (params?.userId) {
    queryParams.append('userId', params.userId.toString());
  }
  
  // Bay filter (backend filtering)
  if (params?.bayIds && params.bayIds.length > 0) {
    params.bayIds.forEach(bayId => {
      queryParams.append('bayIds', bayId.toString());
    });
  }
  
  // Restriction collection mode
  if (params?.collectAllRestrictions !== undefined) {
    queryParams.append('collectAllRestrictions', params.collectAllRestrictions.toString());
  }

  // Effective slot interval (client-side enforcement, e.g. min 30 even if club is set to 15)
  if (params?.slotIntervalMinutes !== undefined) {
    queryParams.append('slotIntervalMinutes', params.slotIntervalMinutes.toString());
  }

  // Demo flow: answer from the club's demo window instead of public/membership hours
  if (params?.type) {
    queryParams.append('type', params.type);
  }
  
  const queryString = queryParams.toString();
  const url = `/bays/${clubId}/available-bays${queryString ? `?${queryString}` : ''}`;
  
  // Use standard apiClient with full type safety
  return apiClient.get<DurationBayAvailabilityPayload>(url);
}

/**
 * Get availability schedule for a club (all bays or specific bay)
 * 
 * Unified endpoint that returns time slots with their availability states.
 * Can fetch availability across all bays OR filter to a specific bay.
 * 
 * This is the new unified endpoint that replaces the need for separate
 * all-bays vs single-bay queries.
 * 
 * @param clubId - Numeric club ID
 * @param params - Query parameters (date, optional bayId, durations, userId)
 * @returns The availability payload directly (data-or-throw); throws on a non-success envelope.
 *
 * @example
 * ```typescript
 * // Get availability across all bays
 * const availability = await bayService.getClubAvailability(2, {
 *   date: '2024-01-15',
 *   durations: '60,90,120',
 *   userId: 123
 * });
 * availability.slots; // ApiBayTimeSlot[]
 * ```
 */
export async function getClubAvailability(
  clubId: number,
  params: {
    date: string;
    bayId?: number;
    durations?: string;
    slotInterval?: number;
    userId?: number;
    /** Demo flow: the grid comes from the club's demo window (S3). */
    type?: 'demo';
  }
): Promise<ClubAvailabilityPayload> {
  const queryParams = new URLSearchParams({
    date: params.date,
    ...(params.bayId && { bayId: params.bayId.toString() }),
    ...(params.durations && { durations: params.durations }),
    ...(params.slotInterval !== undefined && { slotInterval: params.slotInterval.toString() }),
    ...(params.userId && { userId: params.userId.toString() }),
    ...(params.type && { type: params.type }),
    // Web renders whatever start times arrive, so it takes the union grid: a bay
    // whose start marks sit off the club's rows (:15 only) contributes its own starts. The server defaults to the club
    // grid for clients that render one row per interval (mobile).
    lattice: 'union',
  });

  const result = await apiClient.get<ClubAvailabilityPayload>(
    `/clubs/${clubId}/availability-with-states?${queryParams.toString()}`
  );

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch club availability');
  }

  return result.payload;
}
